import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Footer from '../home/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useServicePagesList, useServicePagesMap } from '../../content/useSiteContent';
import { setByPath } from '../../utils/objectPath';
import { TECH_LOGO_MAP, TECH_LOGO_KEYS } from '../../content/servicePageTemplate';
import Icon from './editable/commonIcon';
import EditableImage from './editable/EditableImage';
import EditablePicker from './editable/EditablePicker';
import EditableFieldSettings from './editable/EditableFieldSettings';
import { buildMobileStyleSheet } from './editable/styleUtils';
import { useEditablePage } from './editable/useEditablePage';
import { useAuth } from '../../context/AuthContext';
import SignInModal from '../auth/SignInModal';
import './TemplateServicePage.css';
import './editable/editable.css';

/* Same EmailJS account/template as the Contact page (service_hhqip4y, Gmail) — the
   previous service_rij8xrc/template_bmxi77f belonged to a different EmailJS account
   nobody currently has the login for, and its "To Email" was misconfigured
   ("recipients address is corrupted"). */
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_hhqip4y';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_ge605zo';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'dlDyPXPu4yKCptTIL';

const LOGO_OPTIONS = TECH_LOGO_KEYS.map((k) => ({ value: k, label: k }));

/**
 * Generic, CMS-driven service page. Renders any `servicePages.pages[slug]` doc.
 * editable=false: plain public render. editable=true: full inline WYSIWYG
 * (used by the admin ServicePageEditor route) with a floating style toolbar
 * per block and a fixed top save bar.
 */
export default function TemplateServicePage({ data, styles, editable = false, onChange, preview = false, currentSlug }) {
  const [openFaq, setOpenFaq] = useState(0);
  const servicePagesList = useServicePagesList();
  const servicePagesMap = useServicePagesMap();
  const relatedServices = useMemo(
    () =>
      servicePagesList
        .filter((entry) => entry.slug !== currentSlug)
        .slice(0, 3)
        .map((entry) => {
          const cardPreview = servicePagesMap[entry.slug]?.data?.cardPreview;
          return {
            slug: entry.slug,
            title: entry.title,
            desc: cardPreview?.shortDescription || '',
            icon: cardPreview?.icon || 'layers',
            to: `/services/${entry.slug}`,
          };
        }),
    [servicePagesList, servicePagesMap, currentSlug]
  );
  const [form, setForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [activeToc, setActiveToc] = useState(data.toc.items[0]?.id);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const pendingSubmitRef = useRef(false);
  const { user, firebaseEnabled, isAuthSatisfied } = useAuth();

  const isEditing = editable && !preview;

  useEffect(() => {
    if (editable) return undefined; // admin route sets its own <title>
    const prevTitle = document.title;
    document.title = data.meta.documentTitle;
    return () => { document.title = prevTitle; };
  }, [editable, data.meta.documentTitle]);

  // In the admin editor, skip the scroll-triggered reveal entirely — content
  // (especially a section just toggled back on) must be visible immediately,
  // not stay at opacity:0 until the admin happens to scroll past it.
  const heroRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const buildRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const whyRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const longformRef = useScrollAnimation({ threshold: 0.05, immediate: isEditing });
  const processRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const techRef = useScrollAnimation({ threshold: 0.2, immediate: isEditing });
  const caseRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const engagementRef = useScrollAnimation({ threshold: 0.05, immediate: isEditing });
  const faqRef = useScrollAnimation({ threshold: 0.05, immediate: isEditing });
  const relatedRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const blogRef = useScrollAnimation({ threshold: 0.05, immediate: isEditing });
  const finalRef = useScrollAnimation({ threshold: 0.05, immediate: isEditing });

  useEffect(() => {
    const sections = data.longform.map((b) => document.getElementById(b.id)).filter(Boolean);
    if (!sections.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setActiveToc(entry.target.id)),
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [data.longform]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /** Builds the EmailJS payload and sends it — the authenticated user's verified email
   * always wins over whatever (if anything) was typed into the form's email field. */
  const sendLead = async () => {
    setIsSubmitting(true);
    try {
      // template_ge605zo (shared with the Contact page) expects your_name/your_email/
      // phone_number/subject/message — this form's fields are CMS-configurable, so map
      // the name/email/phone fields onto those and fold everything else into `message`
      // so no answer is lost even if a custom field has no dedicated slot.
      const formFields = data.finalCta.form.fields || [];
      const nameField = formFields.find((f) => f.key === 'fullName') || formFields.find((f) => f.type === 'text');
      const emailField = formFields.find((f) => f.type === 'email');
      const phoneField = formFields.find((f) => f.type === 'tel');
      const emailValue = user?.email || (emailField ? (form[emailField.key] || '').trim() : '');
      const messageLines = formFields
        .filter((f) => f !== nameField && f !== emailField && f !== phoneField)
        .map((f) => `${f.label}: ${(form[f.key] || '').trim() || '-'}`);
      const payload = {
        your_name: nameField ? (form[nameField.key] || '').trim() : '',
        your_email: emailValue,
        phone_number: phoneField ? (form[phoneField.key] || '').trim() : '',
        subject: `${data.hero.title} — project estimate request`,
        message: messageLines.join('\n'),
        reply_to: emailValue,
      };
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, payload, EMAILJS_PUBLIC_KEY);
      setIsSubmitted(true);
    } catch (err) {
      setFormErr(typeof err?.text === 'string' && err.text ? err.text : 'Something went wrong while sending your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErr('');
    const formFields = data.finalCta.form.fields || [];
    const missing = formFields.some((f) => f.required && !(form[f.key] || '').trim());
    if (missing) {
      setFormErr('Please fill in all required fields.');
      return;
    }
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setFormErr('Email service is not configured. Please contact support.');
      return;
    }

    if (firebaseEnabled && !isAuthSatisfied) {
      pendingSubmitRef.current = true;
      setShowAuthModal(true);
      return; // form state is untouched — nothing typed is lost
    }

    await sendLead();
  };

  // Resumes a submission that was interrupted by the sign-in modal, once the visitor
  // is actually signed in (and verified, for password accounts).
  useEffect(() => {
    if (!pendingSubmitRef.current || !isAuthSatisfied) return;
    pendingSubmitRef.current = false;
    setShowAuthModal(false);
    sendLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthSatisfied]);

  /* ---- editor plumbing (no-ops when editable=false); shared with the About page editor ---- */
  const {
    updateData,
    T,
    RemoveBtn,
    AddBtn,
    SectionToggle,
    showSection,
    isSectionHidden,
    IconPick,
    LinkPick,
  } = useEditablePage({ data, styles, onChange, isEditing });

  /** Shared with the hero's lead-capture card (replaces "At a glance") and the final-CTA section below — one form, one state, rendered in two places so a visitor who starts filling it in the hero can keep going at the bottom. */
  const renderLeadForm = () => {
    if (isSubmitted) {
      const formFields = data.finalCta.form.fields || [];
      const nameField = formFields.find((f) => f.key === 'fullName') || formFields.find((f) => f.type === 'text');
      const emailField = formFields.find((f) => f.type === 'email');
      const firstName = nameField ? (form[nameField.key] || '').split(' ')[0] : '';
      const replyEmail = emailField ? form[emailField.key] || '' : '';
      return (
        <div className="fsd-form__success">
          <span className="fsd-form__success-icon"><Icon name="check" size={24} /></span>
          <h3>Thanks, {firstName || 'there'}!</h3>
          <p>We received your project details and a senior engineer will reply to <strong>{replyEmail}</strong> within 24 hours.</p>
        </div>
      );
    }
    return (
      <form className="fsd-form" onSubmit={handleSubmit}>
        <div>
          {T('finalCta.form.title', 'finalCta.form.title', data.finalCta.form.title, 'h3', 'fsd-form__title')}
          {T('finalCta.form.sub', 'finalCta.form.sub', data.finalCta.form.sub, 'p', 'fsd-form__sub')}
        </div>

        <div className="fsd-form__grid">
          {(data.finalCta.form.fields || []).map((field, i) => {
            const commonWrapClass = `fsd-form__field${field.fullWidth ? ' fsd-form__field--full' : ''}${isEditing ? ' svcpg-form-field-wrap' : ''}`;
            const isLockedEmail = field.type === 'email' && !!user;
            const inputProps = {
              className: field.type === 'textarea' ? 'fsd-form__textarea' : field.type === 'select' ? 'fsd-form__select' : 'fsd-form__input',
              name: field.key,
              value: isLockedEmail ? user.email : form[field.key] || '',
              onChange: handleFormChange,
              required: !!field.required,
              readOnly: isLockedEmail,
            };
            return (
              <label className={commonWrapClass} key={field.key}>
                <span className="fsd-form__label">
                  {T(`finalCta.form.fields.${i}.label`, `finalCta.form.fields.${i}.label`, field.label, 'span')}
                  {field.required ? <span className="fsd-form__required"> *</span> : null}
                  <EditableFieldSettings
                    editable={isEditing}
                    field={field}
                    onChange={(next) => updateData(`finalCta.form.fields.${i}`, next)}
                  />
                </span>
                {field.type === 'textarea' ? (
                  <textarea {...inputProps} placeholder={field.placeholder} />
                ) : field.type === 'select' ? (
                  <select {...inputProps}>
                    <option value="">Select one</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input {...inputProps} type={field.type} placeholder={field.placeholder} />
                )}
                <RemoveBtn path="finalCta.form.fields" index={i} small label="Remove field" />
              </label>
            );
          })}
        </div>
        <AddBtn
          path="finalCta.form.fields"
          label="Add field"
          template={(arr) => ({ key: `field_${Date.now()}_${arr.length}`, type: 'text', label: 'New field', placeholder: '', required: false })}
        />

        {formErr ? <p className="fsd-form__error">{formErr}</p> : null}

        <button type="submit" className="fsd-form__submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending…' : T('finalCta.form.submitLabel', 'finalCta.form.submitLabel', data.finalCta.form.submitLabel, 'span')}
          {!isSubmitting && <Icon name="arrowRight" size={18} />}
        </button>
        <p className="fsd-form__privacy">
          <Icon name="lock" size={15} />
          {T('finalCta.form.privacyText', 'finalCta.form.privacyText', data.finalCta.form.privacyText, 'span')}
        </p>
      </form>
    );
  };

  return (
    <div className="fsd-page">
      <style>{buildMobileStyleSheet(styles, 640)}</style>

      {/* ============ Hero ============ */}
      <section className="fsd-section fsd-section--dark fsd-hero fsd-reveal" ref={heroRef}>
        <nav className="fsd-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="fsd-breadcrumb__chev"><Icon name="chevronRight" size={14} /></span>
          <Link to="/services">Services</Link>
          <span className="fsd-breadcrumb__chev"><Icon name="chevronRight" size={14} /></span>
          {T('hero.breadcrumbCurrent', 'hero.breadcrumbCurrent', data.hero.breadcrumbCurrent, 'span', 'fsd-breadcrumb__current')}
        </nav>

        <div className="fsd-hero__grid">
          <div>
            <p className="fsd-kicker fsd-kicker--on-dark">
              <span className="fsd-kicker__dot" aria-hidden="true" />
              {T('hero.kicker', 'hero.kicker', data.hero.kicker, 'span')}
            </p>
            {T('hero.title', 'hero.title', data.hero.title, 'h1', 'fsd-hero__title')}
            {T('hero.lead', 'hero.lead', data.hero.lead, 'p', 'fsd-hero__lead')}
            <div className="fsd-hero__ctas">
              <Link to={data.hero.primaryCtaTo} className="fsd-btn fsd-btn--primary">
                {LinkPick('hero.primaryCtaTo', data.hero.primaryCtaTo, <Icon name="calendar" size={18} />, 'Primary CTA')}
                {T('hero.primaryCtaLabel', 'hero.primaryCtaLabel', data.hero.primaryCtaLabel, 'span')}
              </Link>
              <Link to={data.hero.secondaryCtaTo} className="fsd-btn fsd-btn--ghost-dark">
                {T('hero.secondaryCtaLabel', 'hero.secondaryCtaLabel', data.hero.secondaryCtaLabel, 'span')}
                {LinkPick('hero.secondaryCtaTo', data.hero.secondaryCtaTo, <Icon name="arrowUpRight" size={18} />, 'Secondary CTA')}
              </Link>
            </div>
            <div className="fsd-hero__tags">
              {data.hero.tags.map((tag, i) => (
                <span className={`fsd-tag${isEditing ? ' svcpg-removable' : ''}`} key={i}>
                  {T(`hero.tags.${i}`, `hero.tags.${i}`, tag, 'span')}
                  <RemoveBtn path="hero.tags" index={i} small label="Remove tag" />
                </span>
              ))}
              <AddBtn path="hero.tags" label="Add tag" template="New tag" />
            </div>
          </div>

          <aside className="fsd-form-wrap fsd-form-wrap--hero">{renderLeadForm()}</aside>
        </div>
      </section>

      {/* ============ Card preview (admin-only; feeds the /services "Core Capabilities" grid) ============ */}
      {isEditing ? (
        <section className="fsd-section fsd-cardpreview-admin">
          <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />Core Capabilities card preview (shown on /services, not on this page)</p>
          <span className="fsd-card__icon" style={{ display: 'inline-flex', marginBottom: 12 }}>
            {IconPick('cardPreview.icon', 'cardPreview.icon', data.cardPreview.icon, 28)}
          </span>
          {T('cardPreview.shortDescription', 'cardPreview.shortDescription', data.cardPreview.shortDescription, 'p', 'fsd-hero__lead')}
          <div className="fsd-hero__tags">
            {data.cardPreview.features.map((feature, i) => (
              <span className="fsd-tag svcpg-removable" key={i}>
                {T(`cardPreview.features.${i}`, `cardPreview.features.${i}`, feature, 'span')}
                <RemoveBtn path="cardPreview.features" index={i} small label="Remove feature" />
              </span>
            ))}
            <AddBtn path="cardPreview.features" label="Add feature" template="New feature" />
          </div>
        </section>
      ) : null}

      {/* ============ What we build ============ */}
      {showSection('whatWeBuild') ? (
      <section className="fsd-section fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('whatWeBuild') ? 'true' : undefined} ref={buildRef}>
        <SectionToggle sectionKey="whatWeBuild" label="What we build" />
        <div className="fsd-heading-row">
          <div>
            <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />{T('whatWeBuild.kicker', 'whatWeBuild.kicker', data.whatWeBuild.kicker, 'span')}</p>
            {T('whatWeBuild.title', 'whatWeBuild.title', data.whatWeBuild.title, 'h2', 'fsd-h2')}
          </div>
          <p className="fsd-heading-row__side">{T('whatWeBuild.side', 'whatWeBuild.side', data.whatWeBuild.side, 'span')}</p>
        </div>
        <div className="fsd-grid-3">
          {data.whatWeBuild.cards.map((card, i) => (
            <article className={`fsd-card${isEditing ? ' svcpg-removable' : ''}`} key={i}>
              <span className="fsd-card__icon">{IconPick(`whatWeBuild.cards.${i}.icon`, `whatWeBuild.cards.${i}.icon`, card.icon, 23)}</span>
              {T(`whatWeBuild.cards.${i}.title`, `whatWeBuild.cards.${i}.title`, card.title, 'h3', 'fsd-card__title')}
              {T(`whatWeBuild.cards.${i}.desc`, `whatWeBuild.cards.${i}.desc`, card.desc, 'p', 'fsd-card__desc')}
              <RemoveBtn path="whatWeBuild.cards" index={i} label="Remove card" />
            </article>
          ))}
        </div>
        <AddBtn
          path="whatWeBuild.cards"
          label="Add card"
          template={{ icon: 'layers', title: 'New capability', desc: 'Describe this capability.' }}
        />
      </section>
      ) : null}

      {/* ============ Why Databiqs ============ */}
      {showSection('whyDatabiqs') ? (
      <section className="fsd-section fsd-section--surface fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('whyDatabiqs') ? 'true' : undefined} ref={whyRef}>
        <SectionToggle sectionKey="whyDatabiqs" label="Why Databiqs" />
        <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />{T('whyDatabiqs.kicker', 'whyDatabiqs.kicker', data.whyDatabiqs.kicker, 'span')}</p>
        {T('whyDatabiqs.title', 'whyDatabiqs.title', data.whyDatabiqs.title, 'h2', 'fsd-h2')}
        <div className="fsd-grid-4" style={{ marginTop: 32 }}>
          {data.whyDatabiqs.cards.map((card, i) => (
            <article className={`fsd-card fsd-card--dark${isEditing ? ' svcpg-removable' : ''}`} key={i}>
              <span className="fsd-card__icon">{IconPick(`whyDatabiqs.cards.${i}.icon`, `whyDatabiqs.cards.${i}.icon`, card.icon, 21)}</span>
              {T(`whyDatabiqs.cards.${i}.title`, `whyDatabiqs.cards.${i}.title`, card.title, 'h3', 'fsd-card__title')}
              {T(`whyDatabiqs.cards.${i}.desc`, `whyDatabiqs.cards.${i}.desc`, card.desc, 'p', 'fsd-card__desc')}
              <RemoveBtn path="whyDatabiqs.cards" index={i} label="Remove card" />
            </article>
          ))}
        </div>
        <AddBtn
          path="whyDatabiqs.cards"
          label="Add card"
          template={{ icon: 'shield', title: 'New reason', desc: 'Describe this reason.' }}
        />
      </section>
      ) : null}

      {/* ============ Long-form + TOC sidebar ============ */}
      {showSection('longform') ? (
      <section className="fsd-section fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('longform') ? 'true' : undefined} ref={longformRef}>
        <SectionToggle sectionKey="longform" label="On this page / long-form content" />
        <div className="fsd-toc-layout">
          <div className="fsd-toc-col">
            <nav className="fsd-toc" aria-label="On this page">
              <p className="fsd-toc__eyebrow">{data.toc.eyebrow}</p>
              {data.toc.items.map((item) => (
                <a key={item.id} href={`#${item.id}`} className={`fsd-toc__link${activeToc === item.id ? ' fsd-toc__link--active' : ''}`}>
                  {item.label}
                </a>
              ))}
              <div className="fsd-toc__card">
                <p className="fsd-toc__card-title">{T('toc.cardTitle', 'toc.cardTitle', data.toc.cardTitle, 'span')}</p>
                <p className="fsd-toc__card-desc">{T('toc.cardDesc', 'toc.cardDesc', data.toc.cardDesc, 'span')}</p>
                <Link to={data.toc.cardCtaTo} className="fsd-btn fsd-btn--primary fsd-btn--sm fsd-btn--block">
                  {T('toc.cardCtaLabel', 'toc.cardCtaLabel', data.toc.cardCtaLabel, 'span')}
                  {LinkPick('toc.cardCtaTo', data.toc.cardCtaTo, <Icon name="arrowRight" size={16} />, 'TOC card CTA')}
                </Link>
              </div>
            </nav>
          </div>

          <div className="fsd-longform">
            {data.longform.map((block, bi) => (
              <div className="fsd-longform__block" id={block.id} key={block.id}>
                {T(`longform.${bi}.title`, `longform.${bi}.title`, block.title, 'h3')}
                {block.paragraphs.map((p, pi) => (
                  <div key={pi} style={{ marginBottom: pi < block.paragraphs.length - 1 ? 16 : 0 }}>
                    {T(`longform.${bi}.paragraphs.${pi}`, `longform.${bi}.paragraphs.${pi}`, p, 'p')}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
      ) : null}

      {/* ============ How we work (dark process) ============ */}
      {showSection('process') ? (
      <section className="fsd-section fsd-section--dark fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('process') ? 'true' : undefined} ref={processRef}>
        <SectionToggle sectionKey="process" label="How we work" />
        <div className="fsd-heading-row">
          <div>
            <p className="fsd-kicker fsd-kicker--on-dark"><span className="fsd-kicker__dot" aria-hidden="true" />{T('process.kicker', 'process.kicker', data.process.kicker, 'span')}</p>
            {T('process.title', 'process.title', data.process.title, 'h2', 'fsd-h2')}
          </div>
          <Link to={data.process.ctaTo} className="fsd-btn fsd-btn--white">
            {T('process.ctaLabel', 'process.ctaLabel', data.process.ctaLabel, 'span')}
            {LinkPick('process.ctaTo', data.process.ctaTo, <Icon name="arrowRight" size={18} />, 'Process CTA')}
          </Link>
        </div>
        <div className="fsd-steps">
          {data.process.steps.map((step, i) => (
            <div className={`fsd-step${isEditing ? ' svcpg-removable' : ''}`} key={i}>
              <div className="fsd-step__top">
                <span className="fsd-step__num">{step.num}</span>
                <span className="fsd-step__rule" aria-hidden="true" />
                <span className="fsd-step__week">{T(`process.steps.${i}.week`, `process.steps.${i}.week`, step.week, 'span')}</span>
              </div>
              {T(`process.steps.${i}.title`, `process.steps.${i}.title`, step.title, 'h3', 'fsd-step__title')}
              {T(`process.steps.${i}.desc`, `process.steps.${i}.desc`, step.desc, 'p', 'fsd-step__desc')}
              <RemoveBtn path="process.steps" index={i} label="Remove step" />
            </div>
          ))}
        </div>
        <AddBtn
          path="process.steps"
          label="Add step"
          template={(arr) => ({ num: String(arr.length + 1).padStart(2, '0'), week: 'Week X', title: 'New step', desc: 'Describe this step.' })}
        />
      </section>
      ) : null}

      {/* ============ Tech stack strip ============ */}
      {showSection('techStack') ? (
      <section className="fsd-section fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('techStack') ? 'true' : undefined} ref={techRef}>
        <SectionToggle sectionKey="techStack" label="Tech stack" />
        <div className="fsd-techstrip">
          <div className="fsd-techstrip__copy">
            <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />{T('techStack.kicker', 'techStack.kicker', data.techStack.kicker, 'span')}</p>
            {T('techStack.title', 'techStack.title', data.techStack.title, 'h2', 'fsd-h2')}
          </div>
          <div className="fsd-techstrip__chips">
            {data.techStack.items.map((t, i) => (
              <span className={`fsd-chip${isEditing ? ' svcpg-removable' : ''}`} key={i}>
                <EditablePicker
                  editable={isEditing}
                  value={t.logoKey}
                  options={LOGO_OPTIONS}
                  onChange={(v) => updateData(`techStack.items.${i}.logoKey`, v)}
                  renderOption={(opt) => <img src={TECH_LOGO_MAP[opt.value]} alt={opt.label} />}
                >
                  <img src={TECH_LOGO_MAP[t.logoKey]} alt={`${t.name} logo`} loading="lazy" />
                </EditablePicker>
                {T(`techStack.items.${i}.name`, `techStack.items.${i}.name`, t.name, 'span')}
                <RemoveBtn path="techStack.items" index={i} small label="Remove tech" />
              </span>
            ))}
            <AddBtn
              path="techStack.items"
              label="Add tech"
              template={{ logoKey: TECH_LOGO_KEYS[0], name: 'New tool' }}
            />
          </div>
        </div>
      </section>
      ) : null}

      {/* ============ Featured case study ============ */}
      {showSection('caseStudy') ? (
      <section className="fsd-section fsd-section--surface fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('caseStudy') ? 'true' : undefined} ref={caseRef}>
        <SectionToggle sectionKey="caseStudy" label="Featured case study" />
        <div className="fsd-case">
          <div className="fsd-case__grid">
            <EditableImage
              blockId="caseStudy.image"
              editable={isEditing}
              src={data.caseStudy.imageSrc}
              alt={data.caseStudy.imageAlt}
              onChange={({ src, alt }) => onChange?.({ data: setByPath(setByPath(data, 'caseStudy.imageSrc', src), 'caseStudy.imageAlt', alt) })}
              className="fsd-case__shot-img"
              style={{ width: '100%', height: '100%', minHeight: 340, borderRadius: 20, objectFit: 'contain', background: '#f4f6fb' }}
              placeholder={
                <div className="fsd-case__shot" role="img" aria-label={data.caseStudy.imageAlt}>
                  <Icon name="image" size={18} />
                  [Product screenshot — alt: {data.caseStudy.imageAlt}]
                </div>
              }
            />
            <div>
              <p className="fsd-kicker" style={{ marginBottom: 8 }}>
                <span className="fsd-kicker__dot" aria-hidden="true" />{T('caseStudy.kicker', 'caseStudy.kicker', data.caseStudy.kicker, 'span')}
              </p>
              {T('caseStudy.projectName', 'caseStudy.projectName', data.caseStudy.projectName, 'h3', 'fsd-case__title')}
              <p className="fsd-case__meta">{T('caseStudy.meta', 'caseStudy.meta', data.caseStudy.meta, 'span')}</p>
              <p className="fsd-case__sub">{T('caseStudy.sub', 'caseStudy.sub', data.caseStudy.sub, 'span')}</p>
              <div className="fsd-case__cols">
                <div>
                  <p className="fsd-case__col-label">{T('caseStudy.challengeLabel', 'caseStudy.challengeLabel', data.caseStudy.challengeLabel, 'span')}</p>
                  <p className="fsd-case__col-text">{T('caseStudy.challengeText', 'caseStudy.challengeText', data.caseStudy.challengeText, 'span')}</p>
                </div>
                <div>
                  <p className="fsd-case__col-label">{T('caseStudy.solutionLabel', 'caseStudy.solutionLabel', data.caseStudy.solutionLabel, 'span')}</p>
                  <p className="fsd-case__col-text">{T('caseStudy.solutionText', 'caseStudy.solutionText', data.caseStudy.solutionText, 'span')}</p>
                </div>
              </div>
              <div className="fsd-case__results">
                {data.caseStudy.results.map((r, i) => (
                  <div className={isEditing ? 'svcpg-removable' : ''} key={i}>
                    <p className="fsd-case__result-value">{T(`caseStudy.results.${i}.value`, `caseStudy.results.${i}.value`, r.value, 'span')}</p>
                    <p className="fsd-case__result-label">{T(`caseStudy.results.${i}.label`, `caseStudy.results.${i}.label`, r.label, 'span')}</p>
                    <RemoveBtn path="caseStudy.results" index={i} small label="Remove result" />
                  </div>
                ))}
                <AddBtn
                  path="caseStudy.results"
                  label="Add metric"
                  template={{ value: '0%', label: 'New metric' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      ) : null}

      {/* ============ Engagement models ============ */}
      {showSection('engagement') ? (
      <section className="fsd-section fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('engagement') ? 'true' : undefined} ref={engagementRef}>
        <SectionToggle sectionKey="engagement" label="Engagement models" />
        <div className="fsd-heading-row">
          <div>
            <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />{T('engagement.kicker', 'engagement.kicker', data.engagement.kicker, 'span')}</p>
            {T('engagement.title', 'engagement.title', data.engagement.title, 'h2', 'fsd-h2')}
          </div>
          <span className="fsd-pill-note">
            <span className="fsd-pill-note__icon"><Icon name="bolt" size={17} /></span>
            {T('engagement.noteText', 'engagement.noteText', data.engagement.noteText, 'span')}
          </span>
        </div>
        <div className="fsd-engagement-grid">
          {data.engagement.tiers.map((tier, i) => (
            <div
              className={`fsd-engagement-card${tier.highlight ? ' fsd-engagement-card--highlight' : ''}${isEditing ? ' svcpg-removable' : ''}`}
              key={i}
            >
              <div className="fsd-engagement-card__head">
                <span className="fsd-engagement-card__icon">{IconPick(`engagement.tiers.${i}.icon`, `engagement.tiers.${i}.icon`, tier.icon, 23)}</span>
                {tier.tag ? <span className="fsd-engagement-card__tag">{T(`engagement.tiers.${i}.tag`, `engagement.tiers.${i}.tag`, tier.tag, 'span')}</span> : null}
              </div>
              {T(`engagement.tiers.${i}.title`, `engagement.tiers.${i}.title`, tier.title, 'h3', 'fsd-engagement-card__title')}
              {T(`engagement.tiers.${i}.desc`, `engagement.tiers.${i}.desc`, tier.desc, 'p', 'fsd-engagement-card__desc')}
              <ul className="fsd-engagement-card__list">
                {tier.bullets.map((b, bi) => (
                  <li className={isEditing ? 'svcpg-removable' : ''} key={bi}>
                    <span className="fsd-check-dot"><Icon name="check" size={13} /></span>
                    {T(`engagement.tiers.${i}.bullets.${bi}`, `engagement.tiers.${i}.bullets.${bi}`, b, 'span')}
                    <RemoveBtn path={`engagement.tiers.${i}.bullets`} index={bi} small label="Remove bullet" />
                  </li>
                ))}
              </ul>
              <AddBtn
                path={`engagement.tiers.${i}.bullets`}
                label="Add bullet"
                template="New benefit"
              />
              <RemoveBtn path="engagement.tiers" index={i} label="Remove tier" />
            </div>
          ))}
        </div>
        <AddBtn
          path="engagement.tiers"
          label="Add tier"
          template={{ icon: 'users', title: 'New tier', desc: 'Describe this engagement model.', bullets: ['New benefit'], highlight: false, tag: '' }}
        />
      </section>
      ) : null}

      {/* ============ FAQ ============ */}
      {showSection('faq') ? (
      <section className="fsd-section fsd-section--surface fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('faq') ? 'true' : undefined} ref={faqRef}>
        <SectionToggle sectionKey="faq" label="FAQ" />
        <div className="fsd-faq-layout">
          <div className="fsd-faq-col">
            <div className="fsd-faq-side">
              <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />{T('faq.kicker', 'faq.kicker', data.faq.kicker, 'span')}</p>
              {T('faq.title', 'faq.title', data.faq.title, 'h2', 'fsd-h2')}
              <p className="fsd-lead">{T('faq.lead', 'faq.lead', data.faq.lead, 'span')}</p>
              <Link to={data.faq.ctaTo} className="fsd-btn fsd-btn--outline">
                {T('faq.ctaLabel', 'faq.ctaLabel', data.faq.ctaLabel, 'span')}
                {LinkPick('faq.ctaTo', data.faq.ctaTo, <Icon name="arrowRight" size={18} />, 'FAQ CTA')}
              </Link>
            </div>
          </div>
          <div className="fsd-faq-list">
            {data.faq.items.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <div className={`fsd-faq-item${isOpen ? ' fsd-faq-item--open' : ''}${isEditing ? ' svcpg-removable' : ''}`} key={index}>
                  <button type="button" className="fsd-faq-item__btn" aria-expanded={isOpen} onClick={() => setOpenFaq(isOpen ? -1 : index)}>
                    <span className="fsd-faq-item__q">{T(`faq.items.${index}.question`, `faq.items.${index}.question`, item.question, 'span')}</span>
                    <span className="fsd-faq-item__toggle"><Icon name="plus" size={18} /></span>
                  </button>
                  {isOpen ? <p className="fsd-faq-item__a">{T(`faq.items.${index}.answer`, `faq.items.${index}.answer`, item.answer, 'span')}</p> : null}
                  <RemoveBtn path="faq.items" index={index} label="Remove FAQ item" />
                </div>
              );
            })}
            <AddBtn
              path="faq.items"
              label="Add FAQ item"
              template={{ question: 'New question?', answer: 'New answer.' }}
            />
          </div>
        </div>
      </section>
      ) : null}

      {/* ============ Related services ============ */}
      {showSection('related') ? (
      <section className="fsd-section fsd-section--surface fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('related') ? 'true' : undefined} ref={relatedRef} style={{ paddingTop: 0 }}>
        <SectionToggle sectionKey="related" label="Related services" />
        <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />{T('related.kicker', 'related.kicker', data.related.kicker, 'span')}</p>
        {T('related.title', 'related.title', data.related.title, 'h2', 'fsd-h2')}
        <div className="fsd-related-grid" style={{ marginTop: 24 }}>
          {relatedServices.map((item) => (
            <Link className="fsd-related-card" to={item.to} key={item.slug}>
              <span className="fsd-related-card__icon"><Icon name={item.icon} size={21} /></span>
              <div>
                <p className="fsd-related-card__title">{item.title}</p>
                <p className="fsd-related-card__desc">{item.desc}</p>
              </div>
              <span className="fsd-related-card__arrow">
                <Icon name="arrowUpRight" size={20} />
              </span>
            </Link>
          ))}
        </div>
      </section>
      ) : null}

      {/* ============ Guides / blog ============ */}
      {showSection('blog') ? (
      <section className="fsd-section fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('blog') ? 'true' : undefined} ref={blogRef}>
        <SectionToggle sectionKey="blog" label="Guides / blog" />
        <div className="fsd-heading-row">
          <div>
            <p className="fsd-kicker"><span className="fsd-kicker__dot" aria-hidden="true" />{T('blog.kicker', 'blog.kicker', data.blog.kicker, 'span')}</p>
            {T('blog.title', 'blog.title', data.blog.title, 'h2', 'fsd-h2')}
          </div>
          <Link to={data.blog.allTo} className="fsd-case__link">
            {T('blog.allLabel', 'blog.allLabel', data.blog.allLabel, 'span')}
            {LinkPick('blog.allTo', data.blog.allTo, <Icon name="arrowRight" size={16} />, 'All articles link')}
          </Link>
        </div>
        <div className="fsd-blog-grid">
          {data.blog.posts.map((post, i) => (
            <Link className={`fsd-blog-card${isEditing ? ' svcpg-removable' : ''}`} to={post.to} key={i}>
              <EditableImage
                blockId={`blog.posts.${i}.image`}
                editable={isEditing}
                src={post.imageSrc}
                alt={post.cover}
                onChange={({ src }) => updateData(`blog.posts.${i}.imageSrc`, src)}
                style={{ width: '100%', height: 200, borderRadius: 16, objectFit: 'cover', marginBottom: 16 }}
                placeholder={
                  <div className="fsd-blog-card__cover" role="img" aria-label={`Cover — ${post.cover}`}>
                    <Icon name="image" size={18} />
                    [Cover — {post.cover}]
                  </div>
                }
              />
              <div className="fsd-blog-card__meta">
                <span className="fsd-blog-card__meta-tag">{T(`blog.posts.${i}.tag`, `blog.posts.${i}.tag`, post.tag, 'span')}</span>
                <span>· {T(`blog.posts.${i}.readTime`, `blog.posts.${i}.readTime`, post.readTime, 'span')}</span>
              </div>
              {T(`blog.posts.${i}.title`, `blog.posts.${i}.title`, post.title, 'h3', 'fsd-blog-card__title')}
              <span className="fsd-blog-card__link">
                {T(`blog.posts.${i}.linkLabel`, `blog.posts.${i}.linkLabel`, post.linkLabel, 'span')}
                {LinkPick(`blog.posts.${i}.to`, post.to, <Icon name="arrowRight" size={16} />, 'Guide')}
              </span>
              <RemoveBtn path="blog.posts" index={i} label="Remove guide" />
            </Link>
          ))}
        </div>
        <AddBtn
          path="blog.posts"
          label="Add guide"
          template={{ tag: 'Guide', title: 'New guide title', to: '/blog-page', cover: 'New guide', imageSrc: '' }}
        />
      </section>
      ) : null}

      {/* ============ Final CTA + lead form ============ */}
      {showSection('finalCta') ? (
      <section className="fsd-section fsd-section--dark fsd-final fsd-reveal" data-svcpg-hidden={isEditing && isSectionHidden('finalCta') ? 'true' : undefined} ref={finalRef} id="fsd-lead-form">
        <SectionToggle sectionKey="finalCta" label="Final CTA + lead form" />
        <div className="fsd-final__grid">
          <div>
            <p className="fsd-kicker fsd-kicker--on-dark"><span className="fsd-kicker__dot" aria-hidden="true" />{T('finalCta.kicker', 'finalCta.kicker', data.finalCta.kicker, 'span')}</p>
            {T('finalCta.title', 'finalCta.title', data.finalCta.title, 'h2', 'fsd-final__title')}
            {T('finalCta.lead', 'finalCta.lead', data.finalCta.lead, 'p', 'fsd-final__lead')}
            <ul className="fsd-final__list">
              {data.finalCta.bullets.map((b, i) => (
                <li className={isEditing ? 'svcpg-removable' : ''} key={i}>
                  <span className="fsd-check-dot"><Icon name="check" size={13} /></span>
                  {T(`finalCta.bullets.${i}`, `finalCta.bullets.${i}`, b, 'span')}
                  <RemoveBtn path="finalCta.bullets" index={i} small label="Remove bullet" />
                </li>
              ))}
              <AddBtn path="finalCta.bullets" label="Add bullet" template="New reassurance" />
            </ul>
          </div>

          <div className="fsd-form-wrap">{renderLeadForm()}</div>
        </div>
      </section>
      ) : null}

      <SignInModal open={showAuthModal} onClose={() => { setShowAuthModal(false); pendingSubmitRef.current = false; }} />

      <Footer />
    </div>
  );
}
